import { Input } from '@zillow/constellation';
import { forwardRef } from 'react';
import { type IMaskInputProps, IMaskMixin } from 'react-imask';

type MaskedInputMixinProps = React.ComponentProps<typeof Input> & IMaskInputProps<HTMLInputElement>;
export type MaskedInputProps = Omit<MaskedInputMixinProps, 'onChange'> & {
  onChange: (value: string) => void;
};

/** Passes the inputRef prop to the Input component */
const MaskedInputMixin = IMaskMixin<HTMLInputElement, MaskedInputMixinProps>(({ inputRef, ...props }) => (
  <Input {...props} ref={inputRef} />
)) as unknown as React.ComponentType<MaskedInputProps>;

/**
 * Returns a masked input compatible with react-hook-forms Controller component
 *
 * @param fixedProps Additional props to be passed to the input
 * @param format
 * Optional. Use this if the mask is insufficient to convert the stored value to the display value.
 * Converts the stored value to the display value, this is what the user sees.
 * If the stored value is invalid, it should return the original stored value
 *
 * @param parse
 * Optional. Use it in combination with "format" if the mask is insufficient to convert the display value to the stored value.
 * Converts the display value to the stored value,
 * if the display value is invalid, it should return the original display value
 * If the "unmask" prop is set to true, this will receive the unmasked value
 */
export function createMaskedInput(
  fixedProps: MaskedInputMixinProps,
  format?: (value: string) => string,
  parse?: (value: string) => string,
) {
  function getDisplayValue(storedValue: string): string;
  function getDisplayValue(storedValue: string | undefined): string | undefined;
  function getDisplayValue(storedValue: string | undefined): string | undefined {
    if (storedValue == null) return storedValue;
    if (!format) return storedValue;
    return format(storedValue) ?? storedValue;
  }

  function getStoredValue(displayValue: string): string {
    if (!parse) return displayValue;
    return parse(displayValue) ?? displayValue;
  }

  const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>(function MaskedInput(
    { onChange, value, ...props },
    ref,
  ) {
    return (
      <MaskedInputMixin
        {...fixedProps}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as any)}
        inputRef={ref}
        prepare={getDisplayValue}
        value={getDisplayValue(value)}
        onAccept={(val: string) => {
          onChange?.(getStoredValue(val));
        }}
      />
    );
  });

  return MaskedInput;
}
